import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface Game {
  id: string;
  title: string;
  cover_image_url: string;
  genre: string;
  developer: {
    display_name: string;
  };
}

export const FeaturedGames = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchFeaturedGames = async () => {
      try {
        const { data, error } = await supabase
          .from('games')
          .select('id, title, cover_image_url, genre, developer:profiles(display_name)')
          .gte('highlight_id', 3)
          .eq('is_published', true)
          .order('highlight_id', { ascending: false });

        if (error) {
          console.error('Error fetching featured games:', error);
          return;
        }

        // Shuffle and take maximum 4 games
        const shuffledGames = ((data as any) || []).sort(() => Math.random() - 0.5);
        setGames(shuffledGames.slice(0, 4));
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedGames();
  }, []);

  useEffect(() => {
    if (!sectionRef.current || games.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [games.length]);

  if (loading) {
    return (
      <section className="py-20 px-4" style={{ marginTop: '-150px', zIndex: 10, position: 'relative' }}>
        <div className="container">
          <h2 className="text-[2.5rem] md:text-[4rem] font-bold text-center mb-12">
            Featured <span className="gradient-text">Games</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4 animate-pulse w-full max-w-sm">
                <div className="aspect-video bg-muted rounded mb-4"></div>
                <div className="h-4 bg-muted rounded mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="py-20 px-4" style={{ marginTop: '-150px', zIndex: 10, position: 'relative' }}>
      <div className="container">
        <h2 className={`text-[2.5rem] md:text-[4rem] font-bold text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          Featured <span className="gradient-text">Games</span>
        </h2>

        <div className="flex flex-wrap justify-center gap-8">
          {games.slice(0, 3).map((game, index) => (
            <Card
              key={game.id}
              className={`overflow-hidden rounded-custom-paddings hover:shadow-lg transition-all duration-700 w-full max-w-sm ${!loading && isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="aspect-video relative">
                <img
                  src={game.cover_image_url || '/placeholder-game.jpg'}
                  alt={game.title}
                  className="w-full h-full object-cover rounded-custom-paddings p-0"
                />
              </div>

              <div className="p-4">
                <h3 className="font-semibold text-lg line-clamp-2">{game.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  by {game.developer?.display_name || 'Unknown Developer'}
                </p>
                <div className="flex flex-wrap gap-1">
                  {game.genre?.split(',').slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag.trim()}
                    </Badge>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
